/**
 * time-zone-options — lista stref czasowych do wyboru w Ustawieniach → „Ogólne”.
 *
 * Źródło: `Intl.supportedValuesOf('timeZone')` (strefy IANA znane przeglądarce); gdy środowisko
 * tego nie ma — co najmniej `DEFAULT_TIME_ZONE`. Etykieta niesie bieżące przesunięcie względem
 * UTC, np. „(UTC+01:00) Europe/Warsaw”; kolejność: po przesunięciu, potem po nazwie.
 */

import { DEFAULT_TIME_ZONE, wallClockIn } from '@saas/shared';

export interface TimeZoneOption {
  value: string;
  label: string;
  /** Przesunięcie względem UTC w minutach w chwili budowania listy. */
  offsetMinutes: number;
}

/** Strefy znane przeglądarce; gdy brak API — sama strefa domyślna. */
export function supportedTimeZones(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] };
  try {
    const zones = intl.supportedValuesOf?.('timeZone');
    if (zones && zones.length > 0) return [...zones];
  } catch {
    // środowisko bez listy stref — niżej strefa domyślna
  }
  return [DEFAULT_TIME_ZONE];
}

/** Przesunięcie strefy względem UTC (w minutach) w podanej chwili. */
export function timeZoneOffsetMinutes(timeZone: string, now: Date): number {
  const wall = wallClockIn(now, timeZone);
  const wallAsUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
  );
  const wholeSeconds = Math.floor(now.getTime() / 1000) * 1000;
  return Math.round((wallAsUtc - wholeSeconds) / 60_000);
}

/** „UTC+01:00” / „UTC-03:30” / „UTC+00:00”. */
export function formatUtcOffset(offsetMinutes: number): string {
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const minutes = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${hours}:${minutes}`;
}

/**
 * Opcje listy: strefy przeglądarki + strefa domyślna + strefy dodatkowe (np. zapisana u tenanta,
 * której przeglądarka nie wypisuje), bez duplikatów, posortowane po przesunięciu i nazwie.
 */
export function buildTimeZoneOptions(
  extra: ReadonlyArray<string | null | undefined> = [],
  now: Date = new Date(),
  zones: ReadonlyArray<string> = supportedTimeZones(),
): TimeZoneOption[] {
  const unique = new Set<string>([DEFAULT_TIME_ZONE, ...zones]);
  for (const zone of extra) if (zone) unique.add(zone);

  const options: TimeZoneOption[] = [];
  for (const value of unique) {
    let offsetMinutes: number;
    try {
      offsetMinutes = timeZoneOffsetMinutes(value, now);
    } catch {
      continue; // strefa nieznana środowisku — pomijamy
    }
    options.push({ value, offsetMinutes, label: `(${formatUtcOffset(offsetMinutes)}) ${value}` });
  }
  return options.sort(
    (a, b) => a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value),
  );
}
